/**
 * GST (Goods & Services Tax) — the shared facts and arithmetic every reimbursement claim, tax
 * invoice, credit note and price preview needs, in one place. Pure functions, no I/O, no Prisma
 * (same rule `billing-pricing.ts` and `@hrms/payroll` follow for money math, for the same reason:
 * this must be computed IDENTICALLY wherever it's used). RN-safe: no Node built-ins.
 *
 * MERGED FROM TWO INDEPENDENT COPIES (2026-09-29): reimbursement (`KEKA_VS_REIMBURSEMENT.md` §3,
 * R-P4) and billing (`KEKA_VS_BILLING.md` §3, B-P1) each built this before the branches synced,
 * as flagged in both plans at the time. The GSTIN checksum algorithm converged independently on
 * the same mod-36 implementation on both sides — reassuring, not coincidental, since it's the
 * published GSTN algorithm either way. This version keeps reimbursement's already-shipped shapes
 * (`gstinState`, `computeGstSplit`'s object input, `validGstRatesOn`, `isItcEligible` — all with
 * real call sites on `main` and, for `gstinState`, already persisted to `expense_claims.place_of_
 * supply` in that exact string format, so changing it would silently split old and new rows onto
 * different representations) and adds billing's own, non-overlapping needs at the bottom
 * (`financialYearLabel`, `amountInWords` — invoice-only, reimbursement has no use for either).
 *
 * NEEDS A CA'S SIGN-OFF BEFORE A REAL INVOICE RELIES ON IT (KEKA_VS_BILLING.md §1.1, D-B6): the
 * SAC code and the exact GST rate for this specific business's registration are still unconfirmed.
 * The checksum itself IS spot-checked against a real, published GSTIN (see gst.test.ts).
 */

import { Decimal } from 'decimal.js';

/** The 36-character alphabet the GSTIN check-digit algorithm indexes into: digits then letters. */
const GSTIN_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Official GST state/UT codes (the first two digits of every GSTIN), per the CBIC state-code list.
 * Keyed by the two-digit string as it appears in a GSTIN, e.g. `'27'` for Maharashtra.
 */
export const GST_STATE_CODES: Record<string, string> = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '25': 'Daman and Diu',
  '26': 'Dadra and Nagar Haveli',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh (old)',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory',
};

/**
 * Structural check: 15 characters, state code (2 digits), PAN (10), entity number (1), the fixed
 * `Z`, and a check character — PLUS the check character itself, computed with the same mod-36
 * algorithm the GSTN portal uses. A pattern-only check (what `expense.contract.ts` did before
 * this file existed) accepts a mistyped GSTIN as long as it merely looks the right shape; this
 * catches a single mistyped or transposed character too.
 */
export function isValidGstin(gstin: string): boolean {
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) return false;
  return gstinCheckDigit(gstin.slice(0, 14)) === gstin[14];
}

/** The check-digit algorithm itself, exported separately so a caller that already validated the
 * pattern (and just wants to compute or re-verify the 15th character) doesn't need to re-run the
 * regex. Alternating weights of 1 and 2 starting from the first character, folding any product of
 * 36 or more back into range exactly the way the GSTN portal's own reference implementation does. */
export function gstinCheckDigit(first14: string): string {
  let factor = 1;
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const codePoint = GSTIN_ALPHABET.indexOf(first14[i]!);
    const product = factor * codePoint;
    sum += Math.floor(product / 36) + (product % 36);
    factor = factor === 1 ? 2 : 1;
  }
  const checksum = (36 - (sum % 36)) % 36;
  return GSTIN_ALPHABET[checksum]!;
}

/** The state a GSTIN belongs to, or null if its state code isn't in the official list — a
 * malformed GSTIN (caught separately by `isValidGstin`) rather than this function's own concern. */
export function gstinState(gstin: string): string | null {
  return GST_STATE_CODES[gstin.slice(0, 2)] ?? null;
}

export interface GstSplitInput {
  /** The amount GST is charged on — the bill's total MINUS its own tax, never the tax-inclusive
   * total. */
  taxableAmount: Decimal;
  /** Percentage, e.g. 18 for 18%. */
  rate: Decimal;
  /** The merchant/supplier's state, from their GSTIN. */
  supplierState: string;
  /** Where the goods/service were supplied TO — the bill's own "place of supply" if it states one,
   * otherwise the claiming legal entity's state (`KEKA_VS_REIMBURSEMENT.md` §3, point 2). */
  placeOfSupplyState: string;
}

export interface GstSplit {
  cgst: Decimal;
  sgst: Decimal;
  igst: Decimal;
  /** Always `cgst + sgst + igst` — computed from the two, never independently rounded, so it can
   * never drift from what CGST+SGST or IGST alone add up to. */
  total: Decimal;
}

/**
 * Same state → CGST + SGST, split equally; different state → IGST, the whole amount. Every
 * component is rounded HALF-UP to the paisa (`KEKA_VS_REIMBURSEMENT.md` §3, point 3) — computed
 * independently would let CGST and SGST each round up and the pair overshoot the intended total by
 * a paisa, which is the exact billing bug this rule exists to fix (`KEKA_VS_BILLING.md` §3, point
 * 4): SGST is deliberately `total - cgst`, not independently rounded, so the two always sum to
 * the total exactly.
 */
export function computeGstSplit(input: GstSplitInput): GstSplit {
  const totalTax = roundHalfUpToPaisa(input.taxableAmount.times(input.rate).dividedBy(100));

  if (input.supplierState === input.placeOfSupplyState) {
    const cgst = roundHalfUpToPaisa(totalTax.dividedBy(2));
    const sgst = totalTax.minus(cgst);
    return { cgst, sgst, igst: new Decimal(0), total: totalTax };
  }

  return { cgst: new Decimal(0), sgst: new Decimal(0), igst: totalTax, total: totalTax };
}

function roundHalfUpToPaisa(value: Decimal): Decimal {
  return value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/**
 * The GST rates actually in force on a given date (`KEKA_VS_REIMBURSEMENT.md` §3, point 4) — an
 * effective-dated list, not a hardcoded one, since a bill dated before a rate-slab change can
 * legitimately carry a rate no longer offered on a new bill today. THE CA SHOULD CONFIRM THIS
 * LIST before it is relied on for a real filing — it is built from the plan's own research, not
 * independently verified against a notification.
 */
const GST_RATE_HISTORY: Array<{ effectiveFrom: string; rates: number[] }> = [
  { effectiveFrom: '2017-07-01', rates: [0, 5, 12, 18, 28] },
  { effectiveFrom: '2025-09-22', rates: [0, 5, 18, 40] },
];

/** The rate slabs valid for a bill dated `billDate` — the most recent entry whose `effectiveFrom`
 * is not after it. Falls back to the earliest entry for a date before GST existed at all, since
 * "no valid rate" is a worse UI failure than a wrong-but-plausible one for data entry this old. */
export function validGstRatesOn(billDate: Date): number[] {
  const applicable = GST_RATE_HISTORY.filter((entry) => new Date(entry.effectiveFrom) <= billDate);
  return (applicable.at(-1) ?? GST_RATE_HISTORY[0]!).rates;
}

/**
 * The nearest valid slab to a RATE BACK-DERIVED from a real bill's `amount`/`gstAmount` (EXP-P2-16,
 * Keka parity) — never exact equality, since the derived figure carries whatever rounding the
 * bill's own two decimal amounts introduce (e.g. 17.9982% from a real invoice that is actually
 * 18%). `null` means genuinely invalid — not close to ANY slab in force on `billDate` — which the
 * caller should refuse, not silently accept as a typo'd GST entry.
 */
export function nearestValidGstRate(rate: Decimal, billDate: Date): number | null {
  const tolerance = 0.5; // percentage points — covers paisa-level rounding on any realistic bill
  let best: { slab: number; distance: Decimal } | null = null;
  for (const slab of validGstRatesOn(billDate)) {
    const distance = rate.minus(slab).abs();
    if (best === null || distance.lessThan(best.distance)) best = { slab, distance };
  }
  return best && best.distance.lessThanOrEqualTo(tolerance) ? best.slab : null;
}

export interface GstAmounts {
  gstAmount: Decimal;
  taxableAmount: Decimal;
  /** Null exactly when GST doesn't apply at all (no split was ever computed) — once GST does
   * apply, `computeGstSplit` always sets all three (the two not in force are zero, not null). */
  cgstAmount: Decimal | null;
  sgstAmount: Decimal | null;
  igstAmount: Decimal | null;
  cessAmount: Decimal | null;
}

/**
 * EXP-P2-16 (Keka parity) — when an approver authorises less than claimed (R-P5), the GST split
 * frozen at submit is still against the ORIGINAL, higher `amount`; left alone, a GL/ITC export
 * would show tax figures that don't reconcile against what is actually being paid. Scales every
 * money field by the same ratio (`approvedAmount / originalAmount`), preserving
 * `computeGstSplit`'s own invariant that the second half of an intrastate pair is the rescaled
 * total MINUS the first, never independently rounded, so CGST+SGST still sum exactly to the
 * rescaled `gstAmount`. `approvedAmount >= originalAmount` (nothing was actually reduced) returns
 * the input unchanged.
 */
export function rescaleGstForApproval(
  original: GstAmounts,
  approvedAmount: Decimal,
  originalAmount: Decimal,
): GstAmounts {
  if (originalAmount.lessThanOrEqualTo(0) || approvedAmount.greaterThanOrEqualTo(originalAmount)) {
    return original;
  }
  const ratio = approvedAmount.dividedBy(originalAmount);
  const scale = (v: Decimal) => v.times(ratio).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const scaleNullable = (v: Decimal | null) => (v === null ? null : scale(v));

  const taxableAmount = scale(original.taxableAmount);
  const gstAmount = scale(original.gstAmount);
  const cessAmount = scaleNullable(original.cessAmount);

  // The same discriminator computeGstSplit's two branches produce: intrastate has a nonzero CGST
  // and a zero IGST, interstate the other way round. Whichever is zero stays zero (scale(0) = 0);
  // the nonzero side scales, and for the intrastate pair SGST is derived rather than scaled
  // independently, so it cannot drift from "total minus CGST" by a rounding paisa.
  const isIntrastate = (original.cgstAmount ?? new Decimal(0)).greaterThan(0);
  const cgstAmount =
    original.cgstAmount === null ? null : isIntrastate ? scale(original.cgstAmount) : new Decimal(0);
  const sgstAmount =
    original.sgstAmount === null ? null : isIntrastate ? gstAmount.minus(cgstAmount!) : new Decimal(0);
  const igstAmount =
    original.igstAmount === null ? null : isIntrastate ? new Decimal(0) : scale(original.igstAmount);

  return { gstAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount };
}

/**
 * Input tax credit eligibility (`KEKA_VS_REIMBURSEMENT.md` §3, point 5): the bill must be
 * addressed to the company's OWN GSTIN (never the employee's, and never blank), and the category
 * must not be a blocked credit under Section 17(5) — food and beverages, club memberships, and
 * similar categories the admin marks `itcEligible: false` when setting up the category.
 */
export function isItcEligible(input: {
  billedToGstin: string | null;
  companyGstin: string | null;
  categoryItcEligible: boolean;
}): boolean {
  if (!input.categoryItcEligible) return false;
  if (!input.billedToGstin || !input.companyGstin) return false;
  return input.billedToGstin === input.companyGstin;
}

// ---------------------------------------------------------------------------
// Financial year — billing only, reimbursement has no per-document numbering series
// ---------------------------------------------------------------------------

/**
 * `26-27` style label for the Indian financial year (1 April to 31 March) a date falls in —
 * what the invoice-numbering series (`INV/26-27/00001`) is keyed by. January–March counts as the
 * SECOND half of the label (e.g. 15 Feb 2027 is still `26-27`), matching `LegalEntity`'s own
 * `fiscalYearStartMonth` convention for an April-start entity (CLAUDE.md invariant 3).
 */
export function financialYearLabel(date: Date): string {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 1-12
  const startYear = month >= 4 ? year : year - 1;
  const twoDigit = (y: number) => (y % 100).toString().padStart(2, '0');
  return `${twoDigit(startYear)}-${twoDigit(startYear + 1)}`;
}

// ---------------------------------------------------------------------------
// Amount in words — billing only, Rule 46's "Amount in Words" invoice line
// ---------------------------------------------------------------------------

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** Converts an integer from 0 to 999 into words — the one repeated building block below. */
function threeDigitsInWords(n: number): string {
  const parts: string[] = [];
  if (n >= 100) {
    parts.push(`${ONES[Math.floor(n / 100)]} Hundred`);
    n %= 100;
  }
  if (n >= 20) {
    parts.push(TENS[Math.floor(n / 10)]!);
    n %= 10;
  }
  if (n > 0) parts.push(ONES[n]!);
  return parts.join(' ');
}

/**
 * The Indian numbering system (lakh, crore — grouping by 2 after the first 3 digits), the
 * convention every GST invoice's "Amount in Words" line uses. `Decimal` in, so a value already
 * validated as a money amount never round-trips through a float on the way to text.
 */
export function amountInWords(amount: string, currencyWord = 'Rupees'): string {
  const value = new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const rupees = value.trunc();
  const paise = value.minus(rupees).times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();

  const rupeeWords = rupeesInWords(rupees.toNumber());
  const paiseWords = paise > 0 ? ` and ${threeDigitsInWords(paise)} Paise` : '';
  return `${currencyWord} ${rupeeWords}${paiseWords} Only`;
}

function rupeesInWords(n: number): string {
  if (n === 0) return 'Zero';
  const groups: { value: number; label: string }[] = [
    { value: Math.floor(n / 10_000_000), label: 'Crore' },
    { value: Math.floor((n / 100_000) % 100), label: 'Lakh' },
    { value: Math.floor((n / 1000) % 100), label: 'Thousand' },
    { value: Math.floor(n % 1000), label: '' },
  ];
  return groups
    .filter((g) => g.value > 0)
    .map((g) => (g.label ? `${threeDigitsInWords(g.value)} ${g.label}` : threeDigitsInWords(g.value)))
    .join(' ');
}
